import type { ReactNode, Ref } from "react";

interface SectionHeadProps {
  children: ReactNode;
  id?: string;
  /** One muted line under the heading. */
  lede?: ReactNode;
  /**
   * `section` is the page's section heading (.tls-h2, 28/600). `block` is a
   * subordinate block on the plan's item step (.tls-h4, 18/600): a tool's
   * steps, a requirements footnote. Keep one size per heading level on a page.
   */
  size?: "section" | "block";
  as?: "h2" | "h3";
  /** For a heading that receives focus after a step completes. */
  ref?: Ref<HTMLHeadingElement>;
  tabIndex?: number;
}

/**
 * The one section heading, with an optional muted lede. Nothing sits above
 * it: owner ruling 2026-10-02 keeps the spaced-capital label for data keys
 * and group labels, never above a title (scripts/qa/label-lint.mjs enforces
 * it).
 *
 * Spacing is the home v2 rhythm: heading to lede 8, block to content 24
 * (16 for a block heading).
 */
export function SectionHead({ children, id, lede, size = "section", as: Tag = "h2", ref, tabIndex }: SectionHeadProps) {
  const block = size === "block";
  return (
    <div className={block ? "mb-4" : "mb-6"}>
      <Tag
        ref={ref}
        id={id}
        tabIndex={tabIndex}
        className={`${block ? "tls-h4" : "tls-h2"} m-0 text-balance${tabIndex === undefined ? "" : " tlsr-focus"}`}
      >
        {children}
      </Tag>
      {lede && <p className="mt-2 max-w-[65ch] text-pretty text-[var(--tls-fg-muted)]">{lede}</p>}
    </div>
  );
}
